if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * Global live region that will NOT be replaced by cart-drawer renderContents.
       * This is the most reliable place to announce add-to-cart success.
       */
      ensureGlobalLiveRegion() {
        let region = document.getElementById('A11yStatusMessage');
        if (!region) {
          region = document.createElement('div');
          region.id = 'A11yStatusMessage';
          region.className = 'visually-hidden';
          region.setAttribute('role', 'status');
          region.setAttribute('aria-live', 'polite');
          region.setAttribute('aria-atomic', 'true');
          document.body.appendChild(region);
        }
        return region;
      }

      announceGlobal(message) {
        if (!message) return;
        const region = this.ensureGlobalLiveRegion();

        // Force re-announce
        region.textContent = '';
        window.setTimeout(() => {
          region.textContent = message;
        }, 30);
      }

      /**
       * Try to announce into the cart drawer live region too, but do it *after*
       * the drawer has re-rendered, and retry briefly because the DOM is replaced.
       */
      announceToCartDrawer(message) {
        if (!message) return;

        const trySet = () => {
          const el = document.getElementById('CartDrawer-LiveRegionText');
          if (!el) return false;

          // Make sure it behaves like a live region even if theme markup is minimal
          el.setAttribute('role', 'status');
          el.setAttribute('aria-live', 'polite');
          el.setAttribute('aria-atomic', 'true');
          el.removeAttribute('aria-hidden');

          // Force re-announce
          el.textContent = '';
          window.setTimeout(() => {
            const el2 = document.getElementById('CartDrawer-LiveRegionText');
            if (!el2) return;
            el2.setAttribute('role', 'status');
            el2.setAttribute('aria-live', 'polite');
            el2.setAttribute('aria-atomic', 'true');
            el2.removeAttribute('aria-hidden');
            el2.textContent = message;

            // Optional: hide again like Dawn does
            window.setTimeout(() => {
              const el3 = document.getElementById('CartDrawer-LiveRegionText');
              if (el3) el3.setAttribute('aria-hidden', 'true');
            }, 1200);
          }, 30);

          return true;
        };

        // Attempt immediately (in case it exists already)
        if (trySet()) return;

        // Otherwise retry a few frames (renderContents often replaces DOM async)
        let attempts = 0;
        const maxAttempts = 15; // ~15 frames (~250ms)
        const tick = () => {
          attempts += 1;
          if (trySet() || attempts >= maxAttempts) return;
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }

      getAddToCartStatusMessage() {
        // Prefer Dawn’s accessible product title patterns if present
        const productTitle =
          this.closest('.product')?.querySelector('.product__title')?.textContent?.trim() ||
          this.closest('.product')?.querySelector('.product__title')?.innerText?.trim() ||
          this.closest('.card')?.querySelector('.card__heading')?.textContent?.trim() ||
          '';

        return productTitle ? `${productTitle} added to cart.` : 'Added to cart successfully.';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            this.error = false;

            const statusMessage = this.getAddToCartStatusMessage();

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });

                    // Announce AFTER renderContents() because drawer DOM is replaced
                    requestAnimationFrame(() => {
                      this.announceGlobal(statusMessage);
                      this.announceToCartDrawer(statusMessage);
                    });
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });

              // Announce AFTER renderContents() because drawer DOM is replaced
              requestAnimationFrame(() => {
                this.announceGlobal(statusMessage);
                this.announceToCartDrawer(statusMessage);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          }),
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
